/* ==========================================================================
   STEIGEN motion engine
   Smooth scroll (Lenis) + GSAP ScrollTrigger choreography.
   Fails safe: if libraries are missing or the visitor prefers reduced
   motion, every element is left fully visible and static.
   Re-initialises on Shopify theme editor section events.
   ========================================================================== */
(function () {
  'use strict';

  var html = document.documentElement;

  // External links to the B2B portal open in a new tab
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('a[href^="https://www.steigen.au"], a[href^="https://steigen.au"]').forEach(function (a) {
      a.target = '_blank'; a.rel = 'noopener';
    });
  });

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasLibs = !!(window.gsap && window.ScrollTrigger);

  if (reduce || !hasLibs) {
    html.classList.add('stg-no-motion');
    return;
  }

  html.classList.add('stg-js');
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  gsap.registerPlugin(ScrollTrigger);
  if (window.SplitText) gsap.registerPlugin(window.SplitText);

  var isDesign = html.classList.contains('shopify-design-mode');
  var isTouch = window.matchMedia('(hover: none)').matches;
  var lenis = null;

  /* ---------- smooth scroll ---------- */
  function initLenis() {
    if (isDesign || isTouch || !window.Lenis) return; // keep native scroll in editor and on touch
    html.classList.add('stg-lenis');
    lenis = new window.Lenis({
      lerp: 0.09,
      wheelMultiplier: 1,
      smoothWheel: true,
      // Drawers, modals and anything scrollable inside them scroll natively
      allowNestedScroll: true,
      prevent: function (node) {
        return !!(node && node.closest && node.closest('dialog, [data-lenis-prevent], .cart-drawer__dialog, .menu-drawer'));
      }
    });
    lenis.on('scroll', ScrollTrigger.update);
    // Park smooth scrolling while a drawer or modal is open, so only the drawer moves
    var syncLenis = function () {
      if (document.querySelector('dialog[open]')) lenis.stop();
      else lenis.start();
    };
    new MutationObserver(syncLenis).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    // anchor links
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (id.length < 2) return;
      var el = document.querySelector(id);
      if (el) { e.preventDefault(); lenis.scrollTo(el, { offset: -80 }); }
    });
  }

  /* ---------- scroll progress bar ---------- */
  function initProgress() {
    if (document.querySelector('.stg-progress')) return;
    var bar = document.createElement('div');
    bar.className = 'stg-progress'; bar.innerHTML = '<i></i>';
    document.body.appendChild(bar);
    gsap.to(bar.firstChild, {
      scaleX: 1, ease: 'none',
      scrollTrigger: { start: 0, end: 'max', scrub: 0.3 }
    });
  }

  /* ---------- generic reveals ---------- */
  function initReveals(root) {
    root.querySelectorAll('[data-stg-reveal]').forEach(function (el, i) {
      if (el.dataset.stgBound) return;
      el.dataset.stgBound = '1';
      var delay = el.dataset.stgDelay ? parseInt(el.dataset.stgDelay, 10) : 0;
      el.style.setProperty('--stg-delay', delay + 'ms');
      ScrollTrigger.create({
        trigger: el, start: 'top 88%', once: true,
        onEnter: function () { el.classList.add('is-in'); }
      });
    });
  }

  /* ---------- magnetic buttons ---------- */
  function initMagnetic(root) {
    if (isTouch) return;
    root.querySelectorAll('.stg-btn, .stg-tile__arrow').forEach(function (btn) {
      if (btn.dataset.stgMag) return;
      btn.dataset.stgMag = '1';
      var isBtn = btn.classList.contains('stg-btn');
      var strength = isBtn ? 0.18 : 0.35;
      var cap = isBtn ? 10 : 14; // never drift more than this many px, so the button stays under the cursor
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        x = Math.max(-cap, Math.min(cap, x));
        y = Math.max(-cap, Math.min(cap, y));
        gsap.to(btn, { x: x, y: y, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
      });
      btn.addEventListener('pointerleave', function () {
        gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'power3.out', overwrite: 'auto' });
      });
    });
  }

  /* ---------- HERO ---------- */
  function initHero(section) {
    var media = section.querySelector('.stg-hero__media--desktop img, .stg-hero__media--desktop video');
    var mediaMobile = section.querySelector('.stg-hero__media--mobile img');
    var title = section.querySelector('.stg-hero__title');
    var others = section.querySelectorAll('.stg-hero__eyebrow, .stg-hero__lede, .stg-hero__ctas, .stg-hero__meta');
    var inner = section.querySelector('.stg-hero__inner');
    var tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

    if (media) tl.fromTo(media, { scale: 1.18, filter: 'brightness(0.6)' }, { scale: 1, filter: 'brightness(1)', duration: 2.2, ease: 'power3.out' }, 0);
    if (mediaMobile) tl.fromTo(mediaMobile, { scale: 1.18 }, { scale: 1, duration: 2.2, ease: 'power3.out' }, 0);

    if (title) {
      var chars;
      if (window.SplitText) {
        title.querySelectorAll('.stg-line').forEach(function (line) {
          var split = new window.SplitText(line, { type: 'chars', charsClass: 'stg-char' });
          chars = (chars || []).concat(split.chars);
        });
      }
      if (chars && chars.length) {
        tl.from(chars, { yPercent: 115, rotate: 4, duration: 1.1, stagger: { each: 0.018, from: 'start' } }, 0.35);
      } else {
        tl.from(title, { y: 40, opacity: 0, duration: 1 }, 0.35);
      }
    }
    if (others.length) tl.from(others, { y: 26, opacity: 0, duration: 1, stagger: 0.12 }, 0.9);

    // parallax + fade on scroll out
    var target = media || mediaMobile;
    if (target) {
      gsap.to(target, {
        yPercent: 18, ease: 'none',
        scrollTrigger: { trigger: section, start: 'top top', end: 'bottom top', scrub: true }
      });
    }
    if (inner) {
      gsap.to(inner, {
        yPercent: -12, opacity: 0.15, ease: 'none',
        scrollTrigger: { trigger: section, start: '35% top', end: 'bottom top', scrub: true }
      });
    }
  }

  /* ---------- STATEMENT (scrolls with the page, no pin) ---------- */
  function initStatement(section) {
    var lines = section.querySelectorAll('.stg-statement__line > span');
    var rest = section.querySelectorAll('.stg-statement__eyebrow, .stg-statement__lede, .stg-statement__cta');
    var tl = gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 70%', once: true } });
    if (lines.length) tl.from(lines, { yPercent: 110, opacity: 0, duration: 0.9, stagger: 0.15, ease: 'power3.out' }, 0);
    if (rest.length) tl.from(rest, { y: 24, opacity: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out' }, 0.35);
  }

  /* ---------- PRODUCT RAIL ---------- */
  function initRail(section) {
    var track = section.querySelector('.stg-rail__track');
    var progress = section.querySelector('.stg-rail__progress i');
    var prev = section.querySelector('[data-rail-prev]');
    var next = section.querySelector('[data-rail-next]');
    if (!track) return;

    function step() {
      var card = track.querySelector('.stg-card');
      return card ? card.getBoundingClientRect().width + 14 : track.clientWidth * 0.6;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      if (progress) gsap.set(progress, { scaleX: max > 0 ? track.scrollLeft / max : 0 });
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    }
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // Sideways trackpad swipes move the rail; up and down keeps scrolling the page.
    track.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault(); e.stopPropagation();
        track.scrollLeft += e.deltaX;
      }
    }, { passive: false });
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step() * 2, behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step() * 2, behavior: 'smooth' }); });

    // drag to scroll with a mouse (touch already scrolls natively)
    if (!isTouch) {
      var down = false, startX = 0, startLeft = 0, moved = false;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) { moved = true; track.classList.add('is-dragging'); }
        if (moved) track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        if (moved) { setTimeout(function () { track.classList.remove('is-dragging'); }, 50); }
      });
    }
    update();

    // stagger cards in
    var cards = section.querySelectorAll('.stg-card');
    gsap.from(cards, {
      y: 40, opacity: 0, duration: 0.9, stagger: 0.06, ease: 'power3.out',
      scrollTrigger: { trigger: section, start: 'top 80%', once: true }
    });
  }

  /* ---------- CATEGORIES ---------- */
  function initCategories(section) {
    var tiles = section.querySelectorAll('.stg-tile');
    gsap.from(tiles, {
      y: 50, opacity: 0, duration: 1, stagger: 0.08, ease: 'power3.out',
      scrollTrigger: { trigger: section, start: 'top 78%', once: true }
    });
  }

  /* ---------- EDITORIAL parallax ---------- */
  function initEditorial(section) {
    var img = section.querySelector('.stg-editorial__media img');
    if (!img) return;
    gsap.fromTo(img, { yPercent: -8 }, {
      yPercent: 8, ease: 'none',
      scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true }
    });
  }

  /* ---------- PROOF counters ---------- */
  function initProof(section) {
    section.querySelectorAll('.stg-stat__num[data-target]').forEach(function (el) {
      var target = parseFloat(el.dataset.target);
      var decimals = (el.dataset.target.split('.')[1] || '').length;
      var out = el.querySelector('[data-val]');
      if (!out) return;
      var obj = { v: 0 };
      ScrollTrigger.create({
        trigger: el, start: 'top 85%', once: true,
        onEnter: function () {
          gsap.to(obj, {
            v: target, duration: 1.8, ease: 'power3.out',
            onUpdate: function () { out.textContent = el.dataset.plain ? Math.round(obj.v).toString() : obj.v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }); }
          });
        }
      });
    });
  }

  /* ---------- section router ---------- */
  var inits = {
    'stg-hero': initHero,
    'stg-statement': initStatement,
    'stg-rail': initRail,
    'stg-categories': initCategories,
    'stg-editorial': initEditorial,
    'stg-proof': initProof
  };

  function initSection(section) {
    if (!section || section.dataset.stgInit) return;
    section.dataset.stgInit = '1';
    var type = section.dataset.stgSection;
    initReveals(section);
    initMagnetic(section);
    if (inits[type]) {
      try { inits[type](section); } catch (err) { console.warn('[steigen] motion init failed', type, err); }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-stg-section]').forEach(initSection);
    initReveals(document);
    initMagnetic(document);
    ScrollTrigger.refresh();
  }

  /* ---------- theme editor support ---------- */
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-stg-section]');
    if (s) { delete s.dataset.stgInit; initSection(s); ScrollTrigger.refresh(); }
  });
  document.addEventListener('shopify:section:unload', function () {
    ScrollTrigger.getAll().forEach(function (st) { if (!document.body.contains(st.trigger)) st.kill(); });
  });
  document.addEventListener('shopify:section:reorder', function () { ScrollTrigger.refresh(); });

  window.addEventListener('load', function () { ScrollTrigger.refresh(); });

  /* ---------- go ---------- */
  function boot() {
    initLenis();
    initProgress();
    initAll();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
